/*Bearer ${token}`
 * CustomerReviewPage — public portal for /cs-review/:token
 * Simple 3-step wizard: Verify → Review → Confirm & Submit
 */

import { useState, useCallback, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import { useVisionUIController, setLayout } from "context";
import VuiTypography from "components/VuiTypography";
import { TC, FS, FW } from "../../tokens/text";

const lightTheme = createTheme({
  palette: { mode: "light" },
  typography: { fontFamily: '"Inter", "Plus Jakarta Display", "Helvetica", "Arial", sans-serif' },
});

function usePageLayout() {
  const [, dispatch] = useVisionUIController();
  const { pathname } = useLocation();
  useEffect(() => { setLayout(dispatch, "page"); }, [pathname]);
}

const API_BASE =
  process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1";

const fmt = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
};

async function apiPost(path, data, accessToken) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(accessToken ? { "X-CS-Review-Access": accessToken } : {}) },
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Error ${res.status}`);
  return body;
}

async function apiPatch(path, data, accessToken) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...(accessToken ? { "X-CS-Review-Access": accessToken } : {}) },
    body: JSON.stringify(data),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Error ${res.status}`);
  return body;
}

// ─── Design tokens ────────────────────────────────────────────────────────────
const BG       = "rgba(37,99,235,0.04)";
const CARD_BG  = TC.white;
const BORDER   = "rgba(148,163,184,0.45)";
const BLUE     = TC.link;
const BLUE_LT  = "rgba(37,99,235,0.09)";
const GREEN    = TC.success;
const GREEN_LT = "rgba(5,150,105,0.08)";
const LOCK_CLR = TC.tertiary;
const TEXT     = TC.primary;
const MUTED    = TC.secondary;

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    background: CARD_BG, fontSize: FS.base, fontWeight: FW.medium,
    "& fieldset": { borderColor: BORDER, borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "rgba(37,99,235,0.55)" },
    "&.Mui-focused fieldset": { borderColor: BLUE, borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { color: MUTED, fontWeight: 600 },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE },
  "& .MuiInputBase-input": { color: TEXT },
};

// ─── Shared atoms ──────────────────────────────────────────────────────────────

function Btn({ onClick, disabled, loading, variant = "primary", children, fullWidth }) {
  const isPrimary = variant === "primary";
  return (
    <Box component="button" onClick={onClick} disabled={disabled}
      sx={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 0.8,
        width: fullWidth ? "100%" : undefined,
        px: 3, py: 1.4, borderRadius: "10px",
        border: isPrimary ? "none" : `1.5px solid ${BORDER}`,
        background: isPrimary ? (disabled ? "rgba(148,163,184,0.55)" : BLUE) : CARD_BG,
        color: isPrimary ? TC.white : disabled ? LOCK_CLR : TEXT,
        fontSize: FS.base, fontWeight: FW.bold, cursor: disabled ? "default" : "pointer",
        transition: "all 0.15s",
        "&:hover:not(:disabled)": { background: isPrimary ? TC.linkHover : BLUE_LT, borderColor: isPrimary ? undefined : BLUE, color: isPrimary ? TC.white : BLUE },
      }}>
      {loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : children}
    </Box>
  );
}

// Progress bar — 3 steps
function ProgressBar({ step }) {
  const steps = ["Verify", "Review Details", "Confirm & Submit"];
  return (
    <Box mb={4}>
      <Box display="flex" alignItems="center" gap={0}>
        {steps.map((label, i) => {
          const done    = i < step;
          const current = i === step;
          return (
            <Box key={i} display="flex" alignItems="center" flex={i < steps.length - 1 ? 1 : 0}>
              <Box display="flex" flexDirection="column" alignItems="center" sx={{ minWidth: 64 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  background: done ? GREEN : current ? BLUE : "rgba(148,163,184,0.2)",
                  color: done || current ? TC.white : MUTED,
                  fontSize: "0.8rem", fontWeight: 800, transition: "all 0.2s",
                }}>
                  {done ? "✓" : i + 1}
                </Box>
                <Box sx={{ fontSize: "0.68rem", fontWeight: current ? 800 : 500, color: current ? BLUE : done ? GREEN : MUTED, mt: 0.5, whiteSpace: "nowrap" }}>
                  {label}
                </Box>
              </Box>
              {i < steps.length - 1 && (
                <Box flex={1} mx={0.5} sx={{ height: "2px", background: done ? GREEN : "rgba(148,163,184,0.2)", transition: "background 0.3s", mt: "-14px" }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// A single labelled field row — editable or locked
function FieldRow({ label, value, editable, correction, onCorrection, lockNote }) {
  const [open, setOpen]   = useState(false);
  const [draft, setDraft] = useState(correction || "");

  const save = () => { onCorrection?.(draft); setOpen(false); };

  return (
    <Box mb={2}>
      {/* Label + value + icon */}
      <Box display="flex" alignItems="flex-start" gap={1.5} py={1.2} px={1.5}
        sx={{ borderRadius: "8px", background: editable ? "rgba(37,99,235,0.025)" : "rgba(148,163,184,0.06)", border: `1px solid ${BORDER}` }}>
        <Box flex={1}>
          <Box sx={{ fontSize: "0.68rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", color: MUTED, mb: 0.3 }}>
            {label}
          </Box>
          <Box sx={{ fontSize: "0.95rem", fontWeight: 600, color: TEXT, lineHeight: 1.5 }}>
            {value || "—"}
          </Box>
          {/* Saved correction shown inline */}
          {correction && !open && (
            <Box mt={0.5} sx={{ fontSize: FS.sm, color: BLUE, fontStyle: "italic" }}>
              ✎ Your note: {correction}
            </Box>
          )}
          {/* Lock note */}
          {lockNote && (
            <Box mt={0.4} sx={{ fontSize: "0.72rem", color: LOCK_CLR }}>
              🔒 {lockNote}
            </Box>
          )}
        </Box>
        {/* Edit button (CS01-correctable only) */}
        {editable && !open && (
          <Tooltip title="Flag a correction" placement="top">
            <IconButton size="small" onClick={() => setOpen(true)}
              sx={{ color: correction ? BLUE : LOCK_CLR, mt: 0.2, "&:hover": { color: BLUE } }}>
              <EditIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        )}
        {editable && open && (
          <Tooltip title="Cancel" placement="top">
            <IconButton size="small" onClick={() => { setOpen(false); setDraft(correction || ""); }}
              sx={{ color: LOCK_CLR, mt: 0.2 }}>
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        )}
        {/* Lock icon (read-only) */}
        {!editable && !lockNote && (
          <Box sx={{ color: LOCK_CLR, mt: 0.2, display: "flex", alignItems: "center" }}>
            <LockIcon sx={{ fontSize: "0.95rem" }} />
          </Box>
        )}
      </Box>

      {/* Inline correction input */}
      {open && (
        <Box mt={1} p={1.5} sx={{ borderRadius: "8px", background: BLUE_LT, border: "1.5px solid rgba(37,99,235,0.45)" }}>
          <Box sx={{ fontSize: "0.78rem", fontWeight: 700, color: BLUE, mb: 1 }}>
            What's incorrect about "{label}"?
          </Box>
          <TextField fullWidth size="small" multiline minRows={2}
            placeholder="Describe what needs to be corrected…"
            value={draft} onChange={e => setDraft(e.target.value)}
            sx={INPUT_SX} />
          <Box mt={1}>
            <Btn onClick={save} disabled={!draft.trim()}>Save note</Btn>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── Email gate (Step 0) ──────────────────────────────────────────────────────

function EmailGate({ onVerified }) {
  const { token }         = useParams();
  const [email, setEmail] = useState("");
  const [temporaryCode, setTemporaryCode] = useState("");
  const [loading, setL]   = useState(false);
  const [error, setError] = useState("");

  usePageLayout();

  const submit = async (e) => {
    e?.preventDefault();
    const t = email.trim();
    if (!t) { setError("Please enter your email address."); return; }
    setL(true); setError("");
    try { onVerified(await apiPost(`/cs-review/${token}/verify`, { email: t, temporaryCode })); }
    catch (err) { setError(err?.message || "Verification failed."); }
    finally { setL(false); }
  };

  return (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center"
      sx={{ background: BG, px: 2 }}>
      <Box sx={{ maxWidth: 440, width: "100%", background: CARD_BG, borderRadius: "16px", border: `1.5px solid ${BORDER}`, p: { xs: 3, md: 4 }, boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>

        {/* Brand */}
        <Box textAlign="center" mb={3}>
          <Box sx={{ background: BLUE, borderRadius: "10px", px: 2, py: 1.8, mb: 2.5 }}>
            <VuiTypography sx={{ fontSize: FS.xs, fontWeight: FW.bold, letterSpacing: "0.15em", color: TC.secondary, textTransform: "uppercase", mb: 0.3 }}>LedgerHub</VuiTypography>
            <VuiTypography sx={{ fontSize: FS.lg, fontWeight: FW.bold, color: TC.white }}>Confirmation Statement Review</VuiTypography>
          </Box>
          <Box sx={{ width: 48, height: 48, borderRadius: "50%", background: BLUE_LT, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
            <MailOutlineIcon sx={{ color: BLUE, fontSize: "1.4rem" }} />
          </Box>
          <VuiTypography component="h1" sx={{ fontSize: FS.xl, fontWeight: FW.bold, color: TEXT, mb: 0.5 }}>Verify your identity</VuiTypography>
          <VuiTypography sx={{ fontSize: FS.md, color: MUTED, lineHeight: 1.6 }}>
            Enter the email address this review was sent to.
          </VuiTypography>
        </Box>

        <Box component="form" onSubmit={submit}>
          <TextField fullWidth type="email" label="Email address" placeholder="you@example.com"
            value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
            autoFocus autoComplete="email" InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, ...INPUT_SX }} />
          <TextField fullWidth label="Verification code" placeholder="Code in your email"
            value={temporaryCode} onChange={e => { setTemporaryCode(e.target.value.toUpperCase()); setError(""); }}
            inputProps={{ maxLength: 6, autoCapitalize: "characters" }}
            sx={{ mb: 2, ...INPUT_SX }} />

          {error && (
            <Box mb={2} px={1.5} py={1} sx={{ borderRadius: "8px", background: "rgba(220,38,38,0.06)", border: `1px solid ${TC.error}`, color: TC.error, fontSize: "0.82rem" }}>
              {error}
            </Box>
          )}

          <Btn onClick={submit} disabled={loading || !email.trim() || !temporaryCode.trim()} loading={loading} fullWidth>
            Continue →
          </Btn>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Submitted confirmation screen ────────────────────────────────────────────

function SubmittedScreen({ review, token, accessToken }) {
  const [paying, setPaying] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  usePageLayout();
  const startCheckout = async () => {
    setPaying(true); setPaymentError("");
    try {
      const result = await apiPost(`/cs-review/${token}/checkout`, undefined, accessToken);
      if (result.checkoutUrl) window.location.assign(result.checkoutUrl);
    } catch (err) { setPaymentError(err.message || "Unable to start secure checkout."); }
    finally { setPaying(false); }
  };
  return (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" sx={{ background: BG, px: 2 }}>
      <Box sx={{ maxWidth: 480, width: "100%", background: CARD_BG, borderRadius: "16px", border: `1.5px solid ${BORDER}`, p: { xs: 3, md: 5 }, textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>
        <CheckCircleIcon sx={{ color: GREEN, fontSize: "3.5rem", mb: 1.5 }} />
        <VuiTypography component="h1" sx={{ fontSize: FS.xl, fontWeight: FW.bold, color: TEXT, mb: 1 }}>All done — thank you!</VuiTypography>
        <VuiTypography sx={{ fontSize: FS.base, color: MUTED, lineHeight: 1.8, mb: 2.5 }}>
          Your confirmation statement details have been submitted.
        </VuiTypography>
        {review?.submittedAt && (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.8, px: 2, py: 0.8, borderRadius: "20px", background: GREEN_LT, border: `1px solid ${GREEN}`, fontSize: FS.sm, fontWeight: FW.bold, color: GREEN }}>
            <CheckCircleIcon sx={{ fontSize: "0.95rem" }} />
            Submitted {fmt(review.submittedAt)}
          </Box>
        )}
        {review?.paymentStatus === "paid" ? (
          <Box mt={2} sx={{ fontSize: "0.85rem", color: GREEN, fontWeight: 700 }}>LedgerHub service fee received. Your accountant will now complete the filing process.</Box>
        ) : (
          <Box mt={3} textAlign="left" p={2} sx={{ borderRadius: "10px", background: "rgba(245,158,11,0.08)", border: `1px solid ${TC.warningAmber}` }}>
            <Box sx={{ fontSize: "0.84rem", fontWeight: 800, color: TEXT, mb: 0.7 }}>Payment required before filing</Box>
            <Box sx={{ fontSize: "0.76rem", color: MUTED, lineHeight: 1.6, mb: 1.5 }}>
              You will pay securely on Stripe. This is a LedgerHub firm service fee, not a payment made to Companies House; filing acceptance is separate.
            </Box>
            {paymentError && <Box sx={{ color: TC.error, fontSize: "0.75rem", mb: 1 }}>{paymentError}</Box>}
            <Btn onClick={startCheckout} disabled={paying} loading={paying} fullWidth>Pay securely with Stripe</Btn>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Review wizard (Steps 1–3 after email verify) ─────────────────────────────

function ReviewForm({ review: initialReview, accessToken }) {
  const { token } = useParams();
  const [review, setReview]           = useState(initialReview);
  const [step, setStep]               = useState(0);  // 0=Company, 1=People, 2=Confirm
  const [madeUpTo, setMadeUpTo]       = useState(initialReview.customerChanges?.madeUpTo || initialReview.madeUpTo || "");
  const [customerNotes, setCustomerNotes] = useState(initialReview.customerChanges?.customerNotes || "");
  const [corrections, setCorrections] = useState({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitErr, setSubmitErr]     = useState("");

  usePageLayout();

  const isSubmitted = ["customer_submitted", "agent_approved"].includes(review?.status);

  const setCorrection = useCallback((field, val) => {
    setCorrections(prev => ({ ...prev, [field]: val }));
  }, []);

  const buildNotes = useCallback(() => {
    const lines = Object.entries(corrections).filter(([, v]) => v?.trim()).map(([k, v]) => `• ${k}: ${v}`);
    const base = customerNotes.trim();
    if (!lines.length) return base;
    return [base, "Field corrections:\n" + lines.join("\n")].filter(Boolean).join("\n\n");
  }, [corrections, customerNotes]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true); setSubmitErr("");
    try {
      await apiPatch(`/cs-review/${token}`, { madeUpTo, customerNotes: buildNotes() }, accessToken);
      const updated = await apiPost(`/cs-review/${token}/submit`, undefined, accessToken);
      setReview(updated);
    } catch (err) { setSubmitErr(err?.message || "Submission failed. Please try again."); }
    finally { setSubmitting(false); }
  }, [token, madeUpTo, buildNotes]);

  if (isSubmitted) return <SubmittedScreen review={review} token={token} accessToken={accessToken} />;

  const snap    = review.dataSnapshot || {};
  const address = [snap.registeredAddressObj?.addressLine1, snap.registeredAddressObj?.addressLine2,
    snap.registeredAddressObj?.locality, snap.registeredAddressObj?.region, snap.registeredAddressObj?.postalCode]
    .filter(Boolean).join(", ") || snap.registeredAddress || "—";

  const STEPS = [
    {
      title: "Check your company details",
      subtitle: "Review the information below. SIC codes can be corrected here — other fields require a separate form.",
      content: (
        <Box>
          {review.agentNotes && (
            <Box mb={3} p={2} sx={{ borderRadius: "10px", background: BLUE_LT, border: "1.5px solid rgba(37,99,235,0.45)" }}>
              <Box sx={{ fontSize: "0.68rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: BLUE, mb: 0.5 }}>Note from your accountant</Box>
              <Box sx={{ fontSize: "0.9rem", color: TEXT, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{review.agentNotes}</Box>
            </Box>
          )}
          <FieldRow label="Company Name"        value={snap.companyName} />
          <FieldRow label="Company Number"      value={snap.companyNumber} />
          <FieldRow label="Registered Address"  value={address}
            lockNote="To change your address contact your accountant (form AD01 required)" />
          <FieldRow label="SIC Codes — Business Activity" value={snap.sicCodes?.join(", ") || "—"}
            editable correction={corrections["SIC Codes"]} onCorrection={v => setCorrection("SIC Codes", v)} />
          {snap.confirmationStatementNextDue && (
            <FieldRow label="Next Confirmation Due" value={fmt(snap.confirmationStatementNextDue)} />
          )}
        </Box>
      ),
    },
    {
      title: "Check your directors & shareholders",
      subtitle: "These are read-only. If anything has changed, your accountant needs to file a separate form first.",
      content: (
        <Box>
          {snap.activeDirectors?.length > 0 && (
            <Box mb={2.5}>
              <Box sx={{ fontSize: "0.72rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: MUTED, mb: 1 }}>Directors</Box>
              {snap.activeDirectors.map((d, i) => (
                <FieldRow key={i}
                  label={d.role?.replace(/_/g, " ") || "Director"}
                  value={d.fullName}
                  lockNote="Changes require form AP01 / TM01 / CH01 — notify your accountant" />
              ))}
            </Box>
          )}
          {snap.activePscs?.length > 0 && (
            <Box>
              <Box sx={{ fontSize: "0.72rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: MUTED, mb: 1 }}>Persons with Significant Control</Box>
              {snap.activePscs.map((p, i) => (
                <FieldRow key={i} label="PSC"
                  value={`${p.name}${p.naturesOfControl?.length ? " — " + p.naturesOfControl.map(n => n.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())).join(", ") : ""}`}
                  lockNote="Changes require forms PSC01–PSC09 — notify your accountant" />
              ))}
            </Box>
          )}
          {!snap.activeDirectors?.length && !snap.activePscs?.length && (
            <Box sx={{ color: MUTED, fontSize: "0.9rem", textAlign: "center", py: 4 }}>No officers on record.</Box>
          )}
        </Box>
      ),
    },
    {
      title: "Confirm and submit",
      subtitle: "Check the period end date and add any final notes, then submit to your accountant.",
      content: (
        <Box>
          <Box mb={3}>
            <Box sx={{ fontSize: "0.78rem", fontWeight: 700, color: TEXT, mb: 0.8 }}>Period End Date (Made Up To) *</Box>
            <TextField fullWidth type="date" value={madeUpTo}
              onChange={e => setMadeUpTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 320, ...INPUT_SX }} />
            <Box sx={{ fontSize: "0.72rem", color: MUTED, mt: 0.6 }}>This is the date your annual confirmation covers up to.</Box>
          </Box>

          <Box mb={3}>
            <Box sx={{ fontSize: "0.78rem", fontWeight: 700, color: TEXT, mb: 0.8 }}>Anything else to tell your accountant? <span style={{ fontWeight: 400, color: MUTED }}>(optional)</span></Box>
            <TextField fullWidth multiline minRows={3} maxRows={8}
              placeholder="Any corrections, questions or changes not covered above…"
              value={customerNotes}
              onChange={e => setCustomerNotes(e.target.value)}
              sx={INPUT_SX} />
          </Box>

          {/* Summary of flagged corrections */}
          {Object.entries(corrections).filter(([, v]) => v?.trim()).length > 0 && (
            <Box mb={3} p={1.8} sx={{ borderRadius: "10px", background: "rgba(245,158,11,0.08)", border: `1.5px solid ${TC.warningAmber}` }}>
              <Box sx={{ fontSize: "0.72rem", fontWeight: FW.bold, textTransform: "uppercase", letterSpacing: "0.07em", color: TC.warning, mb: 0.8 }}>
                Corrections flagged
              </Box>
              {Object.entries(corrections).filter(([, v]) => v?.trim()).map(([k, v]) => (
                <Box key={k} sx={{ fontSize: "0.82rem", color: TC.warning, lineHeight: 1.7 }}>
                  <strong>{k}:</strong> {v}
                </Box>
              ))}
            </Box>
          )}

          {submitErr && (
            <Box mb={2} px={1.5} py={1} sx={{ borderRadius: "8px", background: "rgba(220,38,38,0.06)", border: `1px solid ${TC.error}`, color: TC.error, fontSize: "0.82rem" }}>
              {submitErr}
            </Box>
          )}

          <Btn onClick={handleSubmit} disabled={submitting || !madeUpTo} loading={submitting} fullWidth>
            <SendIcon sx={{ fontSize: "1.1rem" }} />
            Submit to accountant
          </Btn>
          <Box sx={{ fontSize: "0.72rem", color: MUTED, textAlign: "center", mt: 1.2 }}>
            By submitting you confirm this information is accurate to the best of your knowledge.
          </Box>
        </Box>
      ),
    },
  ];

  const current = STEPS[step];

  return (
    <Box sx={{ background: BG, minHeight: "100vh", py: { xs: 3, md: 5 }, px: { xs: 2, md: 3 } }}>
      <Box maxWidth={680} mx="auto">

        {/* Header */}
        <Box mb={3} textAlign="center">
          <VuiTypography sx={{ fontSize: FS.xs, fontWeight: FW.bold, letterSpacing: "0.15em", color: MUTED, textTransform: "uppercase", mb: 0.3 }}>LedgerHub</VuiTypography>
          <VuiTypography component="h1" sx={{ fontSize: FS.xl, fontWeight: FW.bold, color: TEXT }}>
            {snap.companyName || "Confirmation Statement Review"}
          </VuiTypography>
          {snap.companyNumber && (
            <Box sx={{ fontSize: "0.85rem", color: MUTED, mt: 0.3 }}>Company No. {snap.companyNumber}</Box>
          )}
        </Box>

        {/* Progress */}
        <ProgressBar step={step + 1} />

        {/* Step card */}
        <Box sx={{ background: CARD_BG, borderRadius: "16px", border: `1.5px solid ${BORDER}`, p: { xs: 2.5, md: 4 }, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", mb: 3 }}>
          <VuiTypography component="h2" sx={{ fontSize: FS.xl, fontWeight: FW.bold, color: TEXT, mb: 0.6 }}>{current.title}</VuiTypography>
          <VuiTypography sx={{ fontSize: FS.md, color: MUTED, mb: 3, lineHeight: 1.65 }}>{current.subtitle}</VuiTypography>
          {current.content}
        </Box>

        {/* Navigation */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            {step > 0 && (
              <Btn variant="ghost" onClick={() => setStep(s => s - 1)}>
                <ArrowBackIcon sx={{ fontSize: "1rem" }} /> Back
              </Btn>
            )}
          </Box>
          {step < STEPS.length - 1 && (
            <Btn onClick={() => setStep(s => s + 1)}>
              Next <ArrowForwardIcon sx={{ fontSize: "1rem" }} />
            </Btn>
          )}
        </Box>

        <Box sx={{ fontSize: "0.68rem", color: MUTED, textAlign: "center", mt: 3 }}>
          Step {step + 1} of {STEPS.length}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CustomerReviewPage() {
  const [review, setReview] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  return (
    <ThemeProvider theme={lightTheme}>
      {!review ? <EmailGate onVerified={({ review: verifiedReview, accessToken: verifiedAccessToken }) => {
        setReview(verifiedReview); setAccessToken(verifiedAccessToken);
      }} /> : <ReviewForm review={review} accessToken={accessToken} />}
    </ThemeProvider>
  );
}
